import React, { useEffect, useState } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  ModalFooter,
  Divider,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { sendClickedOnOneOnOneLifecycleEvent } from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';
import { Select } from 'uiToolkitV1/Select';
import { TextBodyLarge, TextLabel } from 'uiToolkitV1/Typography';

import { AGENDA_CONDITIONS } from '../../constants';
import { OneOnOneSettingsPageReactQueryKeys } from '../../constants/OneOnOneSettingsPageReactQueryKeys';
import { setSaveButtonIsDisabled } from '../../slice/OneOnOneEventBasedAgendaSlice';
import { EventTemplateAgendaType } from '../AgendaPoints/services';

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
  onSave?: () => void;
}

const AddEventModal = ({ isOpen, onClose, onSave }: Props) => {
  const eventOptions = Object.keys(AGENDA_CONDITIONS)
    .map((key) => ({
      label: AGENDA_CONDITIONS[key],
      value: key,
    }))
    .filter(({ value }) => value !== 'REGULAR');

  const eventChangeHandler = (event: { label: string; value: string }) => {
    setSelectedEvent(event);
  };

  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const alreadyAddedEvents = queryClient.getQueryData<EventTemplateAgendaType>([
    OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda,
    'EVENT_BASED',
  ]);

  const alreadyAddedEventsKeys = Object.keys(alreadyAddedEvents ?? {});

  const validEventOptions = eventOptions.filter((event) => !alreadyAddedEventsKeys.includes(event.value));

  const [selectedEvent, setSelectedEvent] = useState(validEventOptions[0]);

  useEffect(() => {
    if (isOpen) {
      setSelectedEvent(validEventOptions[0]);
    }
  }, [isOpen]);

  const onSaveHandler = () => {
    sendClickedOnOneOnOneLifecycleEvent({
      eventProperties: {
        event: selectedEvent.label,
      },
    });
    onSave && onSave();

    queryClient.setQueryData<EventTemplateAgendaType>(
      [OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'EVENT_BASED'],
      (oldData) => ({
        ...oldData,
        [selectedEvent.value]: (oldData && oldData[selectedEvent.value]) ?? [],
      })
    );
    dispatch(
      setSaveButtonIsDisabled({
        saveButtonIsDisabled: false,
      })
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent position="relative">
        <ModalHeader h="64px" position="relative">
          <Flex mt="8px" flexDir="column">
            <TextBodyLarge bold>Add a new event</TextBodyLarge>
            <TextLabel color="#666666">You can add agenda points for specific event based use-cases </TextLabel>
          </Flex>
          <ModalCloseButton
            style={{
              position: 'absolute',
              left: '-3%',
              top: '-20%',
              backgroundColor: '#323846',
              color: 'white',
              borderRadius: '40px',
              width: '28px',
              height: '28px',
              border: '2px solid #f5f7f9',
            }}
          />
        </ModalHeader>
        <Divider w="90%" opacity={1} color="#EBEFF3" m="8px auto" />

        <ModalBody>
          <Box
            sx={{
              '.select__control': {
                width: '400px',
                borderBottom: '1px solid #999999 !important',
              },
              '.select__control:hover': {
                borderBottom: '1px solid #333333',
              },
            }}
            minH="82px"
            display="flex"
            flexDir="column"
            justifyContent="center"
          >
            <Flex
              sx={{
                '.select': {
                  '& .select__single-value': {
                    fontWeight: 'normal', // Override the font weight for the selected value
                  },
                },
              }}
              flexDir="column"
              alignItems="flex-start"
            >
              <TextLabel mb="8px" color="#666666">
                Name of the event{' '}
              </TextLabel>
              {/* @ts-ignore */}
              <Select options={validEventOptions} value={selectedEvent} handleChange={eventChangeHandler} />
            </Flex>
          </Box>
        </ModalBody>
        <ModalFooter
          sx={{
            background: '#F5F7F9',
            borderTop: '1px solid #CED7DF',
            borderRadius: '0px 0px 8px 8px',
          }}
          mt="16px"
        >
          <Flex>
            <Button mr="271px" variant="tertiary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={onSaveHandler}>Add</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { AddEventModal };
